import React, { useEffect, useState, useRef, memo } from 'react';
import { ArrowDown, Star, Play, Pause, Volume2, Zap, Headphones, Smartphone } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
function useCountdown() {
  const getDeadline = () => {
    const stored = localStorage.getItem('memorial-deadline');
    if (stored) {
      const deadline = parseInt(stored, 10);
      if (deadline > Date.now()) return deadline;
    }
    const deadline = Date.now() + 24 * 60 * 60 * 1000 - 1000;
    localStorage.setItem('memorial-deadline', String(deadline));
    return deadline;
  };
  const [deadline] = useState(getDeadline);
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, deadline - Date.now()));
  useEffect(() => {
    const interval = setInterval(() => {
      const remaining = Math.max(0, deadline - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [deadline]);
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor(timeLeft % (1000 * 60 * 60) / (1000 * 60));
  const seconds = Math.floor(timeLeft % (1000 * 60) / 1000);
  return {
    hours: String(hours).padStart(2, '0'),
    minutes: String(minutes).padStart(2, '0'),
    seconds: String(seconds).padStart(2, '0')
  };
}
function PrologueAudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      setCurrentTime(current);
      setProgress(current / total * 100);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (progressBarRef.current && audioRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = Math.max(0, Math.min(100, x / rect.width * 100));
      const newTime = percentage / 100 * audioRef.current.duration;
      audioRef.current.currentTime = newTime;
      setProgress(percentage);
      setCurrentTime(newTime);
    }
  };
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  return <div className="w-full max-w-md mx-auto mb-8 md:mb-10 bg-surface border-2 border-accent-primary/40 rounded-lg p-3 md:p-4 shadow-[0_0_30px_rgba(201,168,76,0.15)] relative overflow-hidden group" data-id="element-364">
      <div className={`absolute inset-0 bg-accent-primary/10 transition-opacity duration-700 pointer-events-none ${isPlaying ? 'opacity-100' : 'opacity-0'}`} data-id="element-365" />

      <audio ref={audioRef} src="https://cdn.magicpatterns.com/uploads/8mFvu6iRP1awtRBf43zPee/THE%20VOICE%20THEY%20COULD%202minutosAUDIO.mp4" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={() => setIsPlaying(false)} className="hidden" data-id="element-366" />

      <div className="relative z-10 flex flex-col gap-3" data-id="element-367">
        <div className="flex items-center justify-between" data-id="element-368">
          <div className="flex items-center gap-2" data-id="element-369">
            <Volume2 className="w-4 h-4 text-accent-primary animate-pulse" data-id="element-370" />
            <span className="font-ui text-[10px] md:text-xs font-bold tracking-widest uppercase text-accent-primary" data-id="element-371">
              Listen to the Prologue
            </span>
          </div>
          <span className="font-mono text-[10px] md:text-xs text-text-muted font-bold" data-id="element-372">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        <div className="flex items-center gap-3 md:gap-4" data-id="element-373">
          <button onClick={togglePlay} className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-gold flex items-center justify-center text-base hover:scale-105 transition-transform duration-300 focus:outline-none shadow-gold-subtle" aria-label={isPlaying ? 'Pause' : 'Play'} data-id="element-374">
            {isPlaying ? <Pause className="w-4 h-4 md:w-5 md:h-5 fill-current" data-id="element-375" /> : <Play className="w-4 h-4 md:w-5 md:h-5 fill-current ml-1" data-id="element-376" />}
          </button>

          {/* Expanded hit area for mobile touch (Fat Finger Rule) */}
          <div className="flex-grow h-8 flex items-center cursor-pointer relative" ref={progressBarRef} onClick={handleProgressClick} data-id="element-377">
            <div className="w-full h-2 md:h-3 bg-elevated rounded-full overflow-hidden border border-border-default relative pointer-events-none" data-id="element-378">
              <div className="absolute top-0 left-0 h-full bg-accent-primary transition-all duration-100 ease-linear" style={{
              width: `${progress}%`
            }} data-id="element-379" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function HeroSection() {
  const countdown = useCountdown();
  const scrollToNext = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col items-center justify-center pt-20 md:pt-24 pb-10 md:pb-12 px-4 md:px-6 bg-base overflow-hidden" data-id="element-380">
      {/* Dramatic Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-accent-primary/10 rounded-full blur-[150px] pointer-events-none" data-id="element-381" />
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-base via-base to-[#0a0908] pointer-events-none -z-10" data-id="element-382" />

      <div className="max-w-5xl mx-auto w-full flex flex-col items-center text-center z-10" data-id="element-383">
        {/* Aggressive Urgency Banner */}
        <FadeIn delay={0.1} className="w-full max-w-2xl mx-auto mb-5 md:mb-8" data-id="element-384">
          <div className="bg-[#B91C1C] text-white font-ui font-bold tracking-widest uppercase py-2.5 md:py-3 px-2 md:px-4 flex justify-center items-center gap-1.5 md:gap-2 text-[9px] sm:text-[10px] md:text-sm shadow-[0_0_25px_rgba(185,28,28,0.4)] border border-red-500/50 rounded-sm transform hover:scale-[1.02] transition-transform" data-id="element-385">
            <Zap className="w-3 h-3 md:w-4 md:h-4 fill-current flex-shrink-0" data-id="element-386" />
            <span className="truncate" data-id="element-387">
              Special Bundle Offer — Limited Time
            </span>
            <Zap className="w-3 h-3 md:w-4 md:h-4 fill-current flex-shrink-0" data-id="element-388" />
          </div>
        </FadeIn>

        {/* Formats Callout */}
        <FadeIn delay={0.2} className="mb-5 md:mb-6" data-id="element-389">
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 font-ui text-[9px] sm:text-[10px] md:text-sm font-bold tracking-widest uppercase text-text-secondary bg-surface/80 border border-border-default px-4 py-1.5 md:px-6 md:py-2 rounded-full" data-id="element-390">
            <span className="flex items-center gap-1.5 md:gap-2" data-id="element-391">
              <Headphones className="w-3 h-3 md:w-4 md:h-4 text-accent-primary" data-id="element-392" />{' '}
              Audiobook
            </span>
            <span className="text-border-accent" data-id="element-393">+</span>
            <span className="flex items-center gap-1.5 md:gap-2" data-id="element-394">
              <Smartphone className="w-3 h-3 md:w-4 md:h-4 text-accent-primary" data-id="element-395" />{' '}
              Instant eBook
            </span>
          </div>
        </FadeIn>

        {/* Massive Headline */}
        <FadeIn delay={0.3} data-id="element-396">
          <h1 className="font-display text-[2.5rem] leading-[1.05] sm:text-5xl md:text-7xl lg:text-8xl font-black text-text-primary tracking-tight mb-4 md:mb-6 uppercase drop-shadow-2xl" data-id="element-397">
            The Voice They <br className="hidden sm:block" data-id="element-398" />
            <span className="text-accent-primary" data-id="element-399">Couldn't Silence</span>
          </h1>
        </FadeIn>

        {/* Punchy Subtitle */}
        <FadeIn delay={0.4} data-id="element-400">
          <p className="relative z-20 font-ui text-sm sm:text-lg md:text-2xl font-bold text-white uppercase tracking-[0.12em] mb-8 md:mb-10 px-2" style={{
          textShadow: '0 2px 8px rgba(0,0,0,1), 0 0 30px rgba(0,0,0,0.8)'
        }} data-id="element-401">
            The Full Story. Unfiltered. Unforgettable.
          </p>
        </FadeIn>

        {/* Book Mockup */}
        <FadeIn delay={0.5} className="relative w-full max-w-[260px] sm:max-w-[320px] md:max-w-md mx-auto mb-8 md:mb-10" data-id="element-402">
          <div className="relative aspect-[4/3] w-full flex justify-center" data-id="element-403">
            <div className="rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.7)] border border-border-muted/30" data-id="element-404">
              <img src="https://cdn.magicpatterns.com/uploads/bx8zXpS1o37kRzPKWiqoqb/mockup.png" alt="The Voice They Couldn't Silence Book Mockup" className="w-full h-full object-cover" data-id="element-405" />
            </div>
          </div>
        </FadeIn>

        {/* Audio Player */}
        <FadeIn delay={0.6} className="w-full" data-id="element-406">
          <PrologueAudioPlayer data-id="element-407" />
        </FadeIn>

        {/* Aggressive CTA Area */}
        <FadeIn delay={0.7} className="w-full max-w-md mx-auto flex flex-col items-center" data-id="element-408">
          <Button href="#offer" className="w-full text-base md:text-xl py-4 md:py-5 mb-5 md:mb-6 shadow-[0_0_40px_rgba(201,168,76,0.3)] animate-pulse hover:animate-none" data-id="element-409">
            GET YOUR COPY NOW &rarr;
          </Button>

          {/* High-Contrast Countdown */}
          <div className="w-full bg-surface border border-border-accent/50 rounded-sm p-3 md:p-4 mb-5 md:mb-6 shadow-lg" data-id="element-410">
            <p className="font-ui text-[10px] md:text-xs font-bold tracking-widest uppercase text-accent-primary mb-2 md:mb-3" data-id="element-411">
              Offer Expires In:
            </p>
            <div className="flex items-center justify-center gap-2 md:gap-4" data-id="element-412">
              <div className="flex flex-col items-center bg-base border border-border-default rounded px-2 md:px-4 py-1.5 md:py-2 min-w-[60px] md:min-w-[70px]" data-id="element-413">
                <span className="font-mono text-2xl md:text-3xl font-bold text-text-primary leading-none tabular-nums" data-id="element-414">
                  {countdown.hours}
                </span>
                <span className="font-ui text-[9px] md:text-[10px] uppercase tracking-widest text-text-muted mt-1" data-id="element-415">
                  Hours
                </span>
              </div>
              <span className="font-mono text-xl md:text-2xl text-accent-primary/50 font-bold" data-id="element-416">
                :
              </span>
              <div className="flex flex-col items-center bg-base border border-border-default rounded px-2 md:px-4 py-1.5 md:py-2 min-w-[60px] md:min-w-[70px]" data-id="element-417">
                <span className="font-mono text-2xl md:text-3xl font-bold text-text-primary leading-none tabular-nums" data-id="element-418">
                  {countdown.minutes}
                </span>
                <span className="font-ui text-[9px] md:text-[10px] uppercase tracking-widest text-text-muted mt-1" data-id="element-419">
                  Mins
                </span>
              </div>
              <span className="font-mono text-xl md:text-2xl text-accent-primary/50 font-bold" data-id="element-420">
                :
              </span>
              <div className="flex flex-col items-center bg-base border border-border-default rounded px-2 md:px-4 py-1.5 md:py-2 min-w-[60px] md:min-w-[70px]" data-id="element-421">
                <span className="font-mono text-2xl md:text-3xl font-bold text-accent-primary leading-none tabular-nums" data-id="element-422">
                  {countdown.seconds}
                </span>
                <span className="font-ui text-[9px] md:text-[10px] uppercase tracking-widest text-text-muted mt-1" data-id="element-423">
                  Secs
                </span>
              </div>
            </div>
          </div>

          {/* Social proof */}
          <div className="flex flex-col items-center gap-1 md:gap-2 mt-1 md:mt-2" data-id="element-424">
            <div className="flex items-center gap-1" data-id="element-425">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 md:w-5 md:h-5 text-accent-primary fill-accent-primary drop-shadow-md" data-id="element-426" />)}
            </div>
            <p className="font-ui text-xs md:text-sm font-bold text-text-secondary tracking-wide uppercase mt-1" data-id="element-427">
              2,400+ Readers · Rated 4.9/5
            </p>
          </div>
        </FadeIn>
      </div>

      <FadeIn delay={1.0} className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:block" data-id="element-428">
        <button onClick={scrollToNext} className="text-text-muted hover:text-accent-primary transition-colors duration-300 animate-bounce" aria-label="Scroll down" data-id="element-429">
          <ArrowDown className="w-8 h-8" data-id="element-430" />
        </button>
      </FadeIn>
    </section>;
}